/**
 * Modal: Atletas inscritos por categoria
 *
 * Esperado da API/Service:
 * - Função listarInscritosPorTorneioAgrupadoPorCategoria(torneioId: string)
 *   que retorna Record<string, InscritoAtleta[]>, onde a chave é o nome da categoria
 *   (ou "Sem categoria" quando não houver categoria associada).
 * - Cada InscritoAtleta contém pelo menos: id, nome, username, imagemUrl?, cidade?, status?
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Avatar from '@mui/material/Avatar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { CircularProgress } from '@mui/material';
import { listarInscritosPorTorneioAgrupadoPorCategoria } from '../../../services/torneioService';
import type { InscritoAtleta } from '../../../types/inscricao';
import { useNavigate } from 'react-router-dom';

export interface InscribedAthletesModalProps {
  open: boolean;
  onClose: () => void;
  tournamentId: string;
}

const InscribedAthletesModal: React.FC<InscribedAthletesModalProps> = ({ open, onClose, tournamentId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<Record<string, InscritoAtleta[]>>({});
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const modalRef = useRef<HTMLDivElement>(null);

  const totalAthletes = useMemo(() => Object.values(groups).reduce((acc, list) => acc + (list?.length || 0), 0), [groups]);

  const sortedCategoryNames = useMemo(() => {
    return Object.keys(groups).sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
  }, [groups]);

  const fetchData = useCallback(async () => {
    if (!tournamentId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await listarInscritosPorTorneioAgrupadoPorCategoria(tournamentId);
      // Ordena atletas por nome em cada categoria
      const ordered: Record<string, InscritoAtleta[]> = {};
      Object.entries(result).forEach(([cat, list]) => {
        ordered[cat] = [...list].sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' }));
      });
      setGroups(ordered);
      // inicia todas categorias expandidas
      const initCollapsed: Record<string, boolean> = {};
      Object.keys(ordered).forEach((k) => { initCollapsed[k] = false; });
      setCollapsed(initCollapsed);
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar atletas inscritos.');
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.classList.add('modal-open');
      setTimeout(() => modalRef.current?.focus(), 0);
      fetchData();
    } else {
      setGroups({});
      setCollapsed({});
      setError(null);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [open, fetchData, onClose]);

  if (!open) return null;

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const toggleCollapse = (cat: string) => setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }));

  const navigateToAthlete = (athleteId: string) => {
    navigate(`/admin/atletas/${athleteId}`);
  };


  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        onMouseDown={handleBackdrop}
        ref={modalRef}
        aria-labelledby="inscribed-athletes-title"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document" >
          <div className="modal-content" style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h5 className="modal-title" id="inscribed-athletes-title">Atletas inscritos por categoria</h5>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose}></button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto' }}>
              {loading && (
                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 160 }}>
                  <CircularProgress size={32} />
                  <span className="ms-3">Carregando...</span>
                </div>
              )}

              {!loading && error && (
                <div className="alert alert-danger d-flex align-items-center" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2" aria-hidden />
                  <div className="flex-grow-1">{error}</div>
                  <button className="btn btn-sm btn-outline-light ms-3" onClick={fetchData}>Tentar novamente</button>
                </div>
              )}

              {!loading && !error && totalAthletes === 0 && (
                <div className="text-center text-muted" style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Nenhum atleta inscrito
                </div>
              )}

              {!loading && !error && sortedCategoryNames.map((catName) => {
                const list = groups[catName] || [];
                const isCollapsed = !!collapsed[catName];
                return (
                  <div key={catName} className="mb-4 border rounded">
                    <button
                      type="button"
                      className="w-100 d-flex justify-content-between align-items-center btn btn-light text-start"
                      onClick={() => toggleCollapse(catName)}
                      aria-expanded={!isCollapsed}
                      aria-controls={`section-${catName}`}
                    >
                      <span className="fw-600">{catName} — {list.length} atleta(s)</span>
                      <i className={`bi ms-2 ${isCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`} />
                    </button>
                    {!isCollapsed && (
                      <div id={`section-${catName}`} className="p-2">
                        <TableContainer component={Paper}>
                          <Table size="small" aria-label={`Atletas da categoria ${catName}`}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Imagem</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>Username</TableCell>
                                <TableCell>Cidade</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {list.map((a) => (
                                <TableRow key={`${catName}-${a.id}-${a.inscricaoId || ''}`} data-testid={`inscribed-athlete-row-${a.id}`} hover onClick={() => navigateToAthlete(a.id)} className='cursor-pointer'>
                                  <TableCell width={56}>
                                    <Avatar src={a.imagemUrl || undefined} alt={a.nome} sx={{ width: 32, height: 32 }} />
                                  </TableCell>
                                  <TableCell>{a.nome}</TableCell>
                                  <TableCell className="text-muted">{a.username ? `@${a.username}` : ''}</TableCell>
                                  <TableCell>{a.cidade || ''}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary ms-auto" onClick={onClose}>Fechar</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InscribedAthletesModal;

